// Request routing against the manifest (szzt-spec.md §9.9), first match wins.
//
// Pure: given a signed index and a request, decide which resource (or redirect / 404) answers it.
// Every content result names an in-manifest path, so the served bytes are always hash-verified as
// normal — routing never serves unverified content. Order (§9.9):
//   1. redirects & rewrites (listed order; `from` exact or a single trailing `*` splat)
//   2. exact resource
//   3. SPA fallback (navigations only)
//   4. notFound (custom or default 404)

import { isSafeHeaderValue } from "./headers.ts";
import type { SzztIndex, RouteResult, ServeRequest } from "./types.ts";

/** Normalize a request path to an absolute, `/`-rooted key matching manifest paths. */
function normalizePath(p: string): string {
  if (p === "") return "/";
  return p.startsWith("/") ? p : `/${p}`;
}

/** A routing `from` matches a path exactly, or as a prefix when it ends in a single trailing `*` (§9.9). */
function matchRule(from: string, path: string): boolean {
  if (from.endsWith("*")) return path.startsWith(from.slice(0, -1));
  return from === path;
}

function notFound(index: SzztIndex): RouteResult {
  const custom = index.routing?.notFound;
  if (custom !== undefined) {
    const loc = index.resources.get(custom);
    if (loc) return { kind: "notFound", status: 404, location: loc };
  }
  return { kind: "notFound", status: 404 };
}

/** Resolve a request path against the signed manifest per §9.9. */
export function route(index: SzztIndex, req: ServeRequest): RouteResult {
  const path = normalizePath(req.path);
  const routing = index.routing;

  // 1. Redirects & rewrites, in listed order; first match wins.
  for (const rule of routing?.redirects ?? []) {
    if (!matchRule(rule.from, path)) continue;
    if (rule.status >= 300 && rule.status < 400) {
      // 3xx: Location of `to`, no body. A CR/LF or control char in `to` would crash `res.writeHead()`
      // (roadmap H2) — fail closed to the same "defensively 404" idiom the missing-rewrite-target case
      // below uses, rather than ever handing an unsafe value up as a redirect Location.
      if (!isSafeHeaderValue(rule.to)) return notFound(index);
      return { kind: "redirect", status: rule.status, location: rule.to };
    }
    // 200 rewrite (the default): serve the resource at `to` exactly once, no re-entry into the table.
    const loc = index.resources.get(rule.to);
    if (loc) return { kind: "resource", status: 200, path: rule.to, location: loc };
    // A build rejects a rewrite whose `to` is absent (§9.9); defensively 404 if it ever happens.
    return notFound(index);
  }

  // 2. Exact resource.
  const exact = index.resources.get(path);
  if (exact) return { kind: "resource", status: 200, path, location: exact };

  // 3. SPA fallback — navigations only; asset requests fall through (§9.9).
  if (req.navigation && routing?.fallback !== undefined) {
    const loc = index.resources.get(routing.fallback);
    if (loc) return { kind: "resource", status: 200, path: routing.fallback, location: loc };
  }

  // 4. Not found.
  return notFound(index);
}
